import { TrackerExtraInfo, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useCallback, useMemo } from 'react';
import { RollingDispatchParams } from 'state-hooks/rolling/useRollingDispatch';
import { RollingAction } from '@diritto/common/rolling/constants';
import { RollingMoreMenu } from 'app-components/rolling/RollingMoreMenu';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { Link } from 'ui-components/common/Link';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { Color } from '@diritto/common/style/color';
import { getTextLineStyle } from 'styles/common';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { useRouter } from 'next/navigation';
import { RollingRelayDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';

interface RollingItemProps {
  rolling: RollingRelayDto;
  profile: ProfileLitePublicRelayDto;
  showDescLines: number;
  onDispatchAction?: (params: RollingDispatchParams) => void;
  showMoreMenu?: boolean;
  trackerExtraInfo: TrackerExtraInfo;
  onClick?: () => void;
  className?: string;
}

const getActions = ({ myProfileId, rollingProfileId }: { myProfileId?: number; rollingProfileId: number }) => {
  // 로그인 안한 상태
  if (!myProfileId) {
    return [];
  }

  return rollingProfileId === myProfileId ? [RollingAction.GoToEdit, RollingAction.Delete] : [];
};

export const RollingItem = ({
  rolling,
  profile,
  showDescLines,
  trackerExtraInfo,
  onDispatchAction,
  showMoreMenu,
  onClick,
  className,
}: RollingItemProps) => {
  const { tracker } = useTrackingState();
  const router = useRouter();

  const { profile: myProfile } = useAuthState();
  const actions = useMemo(
    () =>
      getActions({
        rollingProfileId: rolling.profileId,
        myProfileId: myProfile?.id,
      }),
    [myProfile?.id, rolling.profileId],
  );

  const handleClickToProfile = useCallback(() => {
    if (profile) {
      tracker.track(TrackingEventType.ClickToProfileDetail, { profileId: profile.id, trackerExtraInfo });
    }
  }, [profile, tracker, trackerExtraInfo]);

  const handleClick = useCallback(() => {
    tracker.track(TrackingEventType.ClickToRollingAllList, { rollingId: rolling.id, trackerExtraInfo });
    router.push(`/rollings/${rolling.id}`);
    onClick?.();
  }, [onClick, rolling.id, router, tracker, trackerExtraInfo]);

  return (
    <StyledContainer onClick={handleClick} className={className}>
      <StyledTitle>{rolling.title}</StyledTitle>
      <StyledDescriptionWrapper>
        <StyledDescription showDescLines={showDescLines}>{rolling.description}</StyledDescription>
      </StyledDescriptionWrapper>
      <StyledProfileWrapper>
        <StyledProfile>
          <Link href={`/accounts/profiles/${profile.id}`} onClick={handleClickToProfile}>
            <ProfileImage file={profile.profileImage} defaultImageKey={profile.nickname} size={20} />
          </Link>
        </StyledProfile>
        <StyledName>
          <Link href={`/accounts/profiles/${profile.id}`} onClick={handleClickToProfile}>
            {profile.nickname}
          </Link>
        </StyledName>
        {showMoreMenu && actions.length > 0 && onDispatchAction && (
          <RollingMoreMenu rolling={rolling} onDispatchAction={onDispatchAction} actions={actions} />
        )}
      </StyledProfileWrapper>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 16px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;

  border-radius: 4px;
  border: 1px solid ${Color.Primary200};

  cursor: pointer;
`;

const StyledTitle = styled.div`
  ${Typography.XSmall};
  font-weight: ${FontWeight.Bold};
  ${getTextLineStyle(1)};
`;

const StyledDescriptionWrapper = styled.div`
  flex: 1;
`;

const StyledDescription = styled.div`
  color: ${Color.Gray700};
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Medium};
  ${({ showDescLines }: { showDescLines: number }) => getTextLineStyle(showDescLines)};
`;

const StyledProfileWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StyledProfile = styled.div`
  flex-shrink: 0;
`;

const StyledName = styled.div`
  display: flex;
  align-items: center;
  color: ${Color.Gray600};
  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Medium};

  & > a {
    margin-right: 4px;
    width: fit-content;
    ${getTextLineStyle(1)};
  }
`;
